import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { api } from '../../api/client';
import { queryKeys } from '../../api/queryKeys';
import type { AlertSummary } from '../../api/hooks';
import { Segmented } from '../../components/Segmented';
import { relative } from '../../lib/format';
import type { Section, StatusDTO } from './system/kit';
import { Overview } from './system/Overview';
import { Services } from './system/Services';
import { Workers } from './system/Workers';
import { Logging, Logs } from './system/Logs';
import { Jobs } from './system/Jobs';
import { StorageView } from './system/StorageView';
import { HandoffCleanup, Housekeeping, TempFiles } from './system/Housekeeping';
import { GraphicsSettings } from './system/GraphicsSettings';
import { RecycleBinSettings } from '../../components/RecycleBinSettings';
import { EmailStatus } from './system/EmailStatus';

/** Anchors inside a section: /admin/status#recycle-bin opens Housekeeping at its Recycle bin section, #temp-files at Temporary files. */
const ANCHOR_SECTION: Record<string, Section> = { 'recycle-bin': 'housekeeping', 'temp-files': 'housekeeping' };
const SECTIONS: Section[] = ['overview', 'services', 'workers', 'logs', 'jobs', 'storage', 'housekeeping', 'email', 'graphics'];

function sectionFor(hash: string): Section | null {
  const key = hash.replace(/^#/, '');
  return ANCHOR_SECTION[key] ?? (SECTIONS.includes(key as Section) ? key as Section : null);
}

export function SystemStatus() {
  const { hash } = useLocation();
  const [section, setSection] = useState<Section>(() => sectionFor(hash) ?? 'overview');
  const status = useQuery({ queryKey: ['system-status'], queryFn: () => api<StatusDTO>('/api/v1/system/status'), refetchInterval: 10_000 });
  const s = status.data;
  const loaded = s !== undefined;
  // The Admin rail badge (1.2.8) follows this page's alerts, so the two always agree.
  const qc = useQueryClient();
  useEffect(() => {
    if (!s) return;
    qc.setQueryData<AlertSummary>(queryKeys.alertSummary(), { errors: s.alerts.filter((a) => a.level === 'error').length, warnings: s.alerts.filter((a) => a.level !== 'error').length });
  }, [s, qc]);
  useEffect(() => { const to = sectionFor(hash); if (to) setSection(to); }, [hash]);
  // Scroll to the anchor once its section has rendered (the section's own data may still be loading, so retry briefly).
  useEffect(() => {
    const id = hash.replace(/^#/, '');
    if (!id || !loaded || !ANCHOR_SECTION[id]) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ block: 'start' }); window.clearInterval(timer); } else if (++tries > 40) window.clearInterval(timer);
    }, 50);
    return () => window.clearInterval(timer);
  }, [hash, loaded]);
  return (
    <div className="page" data-testid="system-status">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={{ flex: 1, margin: 0 }}>System status</h2>
        {s && <span className="faint" style={{ fontSize: 12 }}>Checked {relative(s.checkedAt)}</span>}
        <button className="btn ghost sm" onClick={() => void status.refetch()}><RefreshCw size={13} /> Refresh</button>
      </div>
      <Segmented role="tablist" style={{ margin: '14px 0 18px' }} buttonRole="tab" value={section} onChange={setSection}
        options={SECTIONS.map((k) => ({ value: k, label: k[0]!.toUpperCase() + k.slice(1), testId: `status-${k}` }))} />
      {!s ? <div className="skeleton" style={{ height: 200 }} /> : (
        <>
          {section === 'overview' && <Overview s={s} go={setSection} />}
          {section === 'services' && <Services s={s} />}
          {section === 'workers' && <Workers s={s} />}
          {section === 'logs' && <><Logging /><Logs /></>}
          {section === 'jobs' && <Jobs />}
          {section === 'storage' && <StorageView />}
          {section === 'housekeeping' && <><Housekeeping /><RecycleBinSettings /><TempFiles /><HandoffCleanup /></>}
          {section === 'email' && <EmailStatus />}
          {section === 'graphics' && <GraphicsSettings />}
        </>
      )}
    </div>
  );
}
